import { useState, useEffect, useCallback } from "react";
import { landingPages as api, type LandingPage, type LandingPageSubmission } from "@/lib/api";
import Spinner from "@/components/Spinner";
import StatusBadge from "@/components/StatusBadge";
import {
  Plus, Globe, Eye, EyeOff, Trash2, X, ExternalLink,
  FileText, Users, BarChart3, Copy, Check, Loader2,
  ArrowLeft, ChevronRight, Palette, Settings, Mail,
  Type, Layout, Zap,
} from "lucide-react";

type View = "list" | "create" | "edit" | "submissions";

import { API_URL, SITE_URL } from "@/lib/siteConfig";
const API_BASE = API_URL;
// SITE_URL imported from siteConfig above

export default function LandingPagesPage() {
  const [view, setView] = useState<View>("list");
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);

  // Create
  const [templates, setTemplates] = useState<{ id: string; name: string; description: string }[]>([]);
  const [createTitle, setCreateTitle] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createTemplate, setCreateTemplate] = useState("lead_magnet");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit
  const [editPage, setEditPage] = useState<LandingPage | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Submissions
  const [subPage, setSubPage] = useState<LandingPage | null>(null);
  const [submissions, setSubmissions] = useState<LandingPageSubmission[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);

  const loadPages = useCallback(async () => {
    try {
      const res = await api.list();
      setPages(res.pages);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  // Auto-generate slug from title
  function handleTitleChange(title: string) {
    setCreateTitle(title);
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 100);
    setCreateSlug(slug);
  }

  async function handleCreate() {
    if (!createTitle.trim() || !createSlug.trim()) { setCreateError("Title and slug required"); return; }
    setCreating(true); setCreateError(null);
    try {
      const res = await api.create({ title: createTitle.trim(), slug: createSlug.trim(), template: createTemplate });
      setEditPage(res.page);
      setView("edit");
      setCreateTitle(""); setCreateSlug("");
      loadPages();
    } catch (e: any) { setCreateError(e.message || "Failed to create"); }
    finally { setCreating(false); }
  }

  async function handleSave() {
    if (!editPage) return;
    setSaving(true);
    try {
      const res = await api.update(editPage.id, {
        title: editPage.title,
        blocks: editPage.blocks,
        form_config: editPage.form_config,
        style_config: editPage.style_config,
        meta_title: editPage.meta_title || undefined,
        meta_description: editPage.meta_description || undefined,
      });
      setEditPage(res.page);
      loadPages();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  }

  async function handlePublish() {
    if (!editPage) return;
    setPublishing(true);
    try {
      if (editPage.status === "published") {
        await api.unpublish(editPage.id);
        setEditPage({ ...editPage, status: "draft", published_at: null });
      } else {
        await api.publish(editPage.id);
        setEditPage({ ...editPage, status: "published", published_at: new Date().toISOString() });
      }
      loadPages();
    } catch (e) { console.error(e); }
    finally { setPublishing(false); }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this page permanently?")) return;
    try { await api.delete(id); loadPages(); }
    catch (e) { console.error(e); }
  }

  async function openEdit(page: LandingPage) {
    try {
      const res = await api.get(page.id);
      setEditPage(res.page);
      setView("edit");
    } catch (e) { console.error(e); }
  }

  async function openSubs(page: LandingPage) {
    setSubPage(page); setSubsLoading(true); setView("submissions");
    try {
      const res = await api.submissions(page.id);
      setSubmissions(res.submissions);
    } catch (e) { console.error(e); }
    finally { setSubsLoading(false); }
  }

  async function loadTemplates() {
    try { const res = await api.templates(); setTemplates(res.templates); }
    catch (e) { console.error(e); }
  }

  function copyUrl(slug: string) {
    const url = `${SITE_URL || window.location.origin}/p/${slug}`;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-20 flex justify-center"><Spinner /></div>;

  // ── Submissions View ──
  if (view === "submissions" && subPage) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => setView("list")} className="flex items-center gap-1.5 text-sm text-slate hover:text-ink mb-6">
          <ArrowLeft size={15} /> Back to Pages
        </button>
        <h1 className="text-xl font-display font-bold text-ink mb-1">{subPage.title} — Submissions</h1>
        <p className="text-xs text-slate mb-6">{subPage.submission_count} total submissions</p>

        {subsLoading ? <Spinner /> : submissions.length === 0 ? (
          <div className="bg-white rounded-xl border border-sand py-12 text-center">
            <Users size={32} className="mx-auto text-slate/20 mb-3" />
            <p className="text-sm text-slate">No submissions yet. Share your page to start collecting leads.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-sand overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-sand bg-paper">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate">Email</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate">Name</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate">Date</th>
              </tr></thead>
              <tbody>
                {submissions.map(s => (
                  <tr key={s.id} className="border-b border-sand/50">
                    <td className="py-2.5 px-4 text-xs text-ink">{s.email}</td>
                    <td className="py-2.5 px-4 text-xs text-slate">{s.name || "—"}</td>
                    <td className="py-2.5 px-4 text-xs text-slate">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ── Edit View ──
  if (view === "edit" && editPage) {
    const pageUrl = `${SITE_URL || window.location.origin}/p/${editPage.slug}`;
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => { setView("list"); setEditPage(null); }}
          className="flex items-center gap-1.5 text-sm text-slate hover:text-ink mb-6">
          <ArrowLeft size={15} /> Back to Pages
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-display font-bold text-ink">{editPage.title}</h1>
            <p className="text-xs text-slate mt-0.5">/{editPage.slug}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
            </button>
            <button onClick={handlePublish} disabled={publishing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                editPage.status === "published"
                  ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                  : "bg-coral text-white hover:bg-coral-dark"
              }`}>
              {publishing ? <Loader2 size={14} className="animate-spin" /> :
                editPage.status === "published" ? <><EyeOff size={14} /> Unpublish</> : <><Globe size={14} /> Publish</>}
            </button>
          </div>
        </div>

        {/* Published URL */}
        {editPage.status === "published" && (
          <div className="bg-teal-light rounded-xl border border-teal/15 p-4 mb-6 flex items-center gap-3">
            <Globe size={16} className="text-teal flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-teal">Live at:</p>
              <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-ink hover:text-coral truncate block">{pageUrl}</a>
            </div>
            <button onClick={() => copyUrl(editPage.slug)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${copied ? "bg-teal text-white" : "bg-white text-teal border border-teal/20"}`}>
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Content editor */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-sand p-5">
              <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-2"><Type size={14} className="text-coral" /> Page Content</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Page Title</label>
                  <input type="text" value={editPage.title}
                    onChange={e => setEditPage({ ...editPage, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Heading</label>
                  <input type="text"
                    value={editPage.blocks?.find((b: any) => b.type === "heading")?.content || ""}
                    onChange={e => {
                      const blocks = [...(editPage.blocks || [])];
                      const hi = blocks.findIndex((b: any) => b.type === "heading");
                      if (hi >= 0) blocks[hi] = { ...blocks[hi], content: e.target.value };
                      else blocks.unshift({ type: "heading", id: "h1", content: e.target.value, level: "h1", align: "center" });
                      setEditPage({ ...editPage, blocks });
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Description Text</label>
                  <textarea rows={3}
                    value={editPage.blocks?.find((b: any) => b.type === "text")?.content || ""}
                    onChange={e => {
                      const blocks = [...(editPage.blocks || [])];
                      const ti = blocks.findIndex((b: any) => b.type === "text");
                      if (ti >= 0) blocks[ti] = { ...blocks[ti], content: e.target.value };
                      else blocks.push({ type: "text", id: "t1", content: e.target.value, align: "center" });
                      setEditPage({ ...editPage, blocks });
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral resize-y" />
                </div>
              </div>
            </div>

            {/* Form settings */}
            <div className="bg-white rounded-xl border border-sand p-5">
              <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-2"><Mail size={14} className="text-coral" /> Form Settings</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Button Text</label>
                  <input type="text" value={editPage.form_config?.buttonText || ""}
                    onChange={e => setEditPage({ ...editPage, form_config: { ...editPage.form_config, buttonText: e.target.value } })}
                    className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Success Message</label>
                  <input type="text" value={editPage.form_config?.successMessage || ""}
                    onChange={e => setEditPage({ ...editPage, form_config: { ...editPage.form_config, successMessage: e.target.value } })}
                    className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Collect Name Field</label>
                  <label className="flex items-center gap-2 text-xs text-slate cursor-pointer">
                    <input type="checkbox"
                      checked={editPage.form_config?.fields?.includes("name") ?? false}
                      onChange={e => {
                        const fields = e.target.checked ? ["email", "name"] : ["email"];
                        setEditPage({ ...editPage, form_config: { ...editPage.form_config, fields } });
                      }}
                      className="accent-coral" />
                    Ask for name in addition to email
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Auto-tag subscribers</label>
                  <input type="text" value={(editPage.form_config?.tags || []).join(", ")}
                    onChange={e => setEditPage({ ...editPage, form_config: { ...editPage.form_config, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) } })}
                    placeholder="e.g. lead-magnet, webinar"
                    className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                  <p className="text-[10px] text-slate mt-0.5">Comma-separated tags applied to new subscribers</p>
                </div>
              </div>
            </div>

            {/* Style */}
            <div className="bg-white rounded-xl border border-sand p-5">
              <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-2"><Palette size={14} className="text-coral" /> Style</h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Background", key: "bgColor" },
                  { label: "Button Color", key: "primaryColor" },
                  { label: "Text Color", key: "textColor" },
                ].map(s => (
                  <div key={s.key}>
                    <label className="block text-[10px] font-semibold text-slate mb-1">{s.label}</label>
                    <input type="color"
                      value={(editPage.style_config as any)?.[s.key] || "#FFFFFF"}
                      onChange={e => setEditPage({ ...editPage, style_config: { ...editPage.style_config, [s.key]: e.target.value } })}
                      className="w-full h-8 rounded border border-sand cursor-pointer" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="bg-white rounded-xl border border-sand p-5 sticky top-24">
            <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-2"><Eye size={14} className="text-coral" /> Preview</h3>
            <div className="rounded-lg overflow-hidden border border-sand/60"
              style={{ backgroundColor: editPage.style_config?.bgColor || "#FFFFFF" }}>
              <div className="p-8 text-center">
                {editPage.blocks?.map((block: any, i: number) => {
                  if (block.type === "heading") return (
                    <h2 key={i} className="text-xl font-display font-bold mb-3"
                      style={{ color: editPage.style_config?.textColor || "#1E2A3A" }}>
                      {block.content || "Your Heading"}
                    </h2>
                  );
                  if (block.type === "text") return (
                    <p key={i} className="text-sm mb-4 leading-relaxed"
                      style={{ color: (editPage.style_config?.textColor || "#1E2A3A") + "99" }}>
                      {block.content || "Your description text"}
                    </p>
                  );
                  if (block.type === "spacer") return <div key={i} style={{ height: block.height || 16 }} />;
                  return null;
                })}

                {/* Form preview */}
                <div className="max-w-xs mx-auto space-y-2 mt-4">
                  <input type="text" placeholder="Email address" disabled
                    className="w-full px-4 py-2.5 rounded-lg border border-black/10 text-sm bg-white/80" />
                  {editPage.form_config?.fields?.includes("name") && (
                    <input type="text" placeholder="Your name" disabled
                      className="w-full px-4 py-2.5 rounded-lg border border-black/10 text-sm bg-white/80" />
                  )}
                  <button disabled className="w-full py-2.5 rounded-lg text-white text-sm font-semibold"
                    style={{ backgroundColor: editPage.style_config?.primaryColor || "#E85D4A" }}>
                    {editPage.form_config?.buttonText || "Submit"}
                  </button>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-slate text-center mt-2">This is a simplified preview. The live page will render fully.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Create View ──
  if (view === "create") {
    if (templates.length === 0) loadTemplates();
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => setView("list")} className="flex items-center gap-1.5 text-sm text-slate hover:text-ink mb-6">
          <ArrowLeft size={15} /> Back
        </button>
        <h1 className="text-xl font-display font-bold text-ink mb-6">Create Landing Page</h1>

        <div className="bg-white rounded-xl border border-sand p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink mb-1">Page Title</label>
            <input type="text" value={createTitle} onChange={e => handleTitleChange(e.target.value)}
              placeholder="e.g. Free Marketing Guide"
              className="w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink mb-1">URL Slug</label>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate">/p/</span>
              <input type="text" value={createSlug} onChange={e => setCreateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="free-marketing-guide"
                className="flex-1 px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink mb-2">Template</label>
            <div className="grid grid-cols-2 gap-2">
              {(templates.length > 0 ? templates : [
                { id: "lead_magnet", name: "Lead Magnet", description: "Free resource download" },
                { id: "webinar", name: "Webinar", description: "Event registration" },
                { id: "waitlist", name: "Waitlist", description: "Coming soon" },
                { id: "newsletter", name: "Newsletter", description: "Subscription" },
                { id: "discount", name: "Discount", description: "Coupon signup" },
                { id: "blank", name: "Blank", description: "Start from scratch" },
              ]).map(t => (
                <button key={t.id} onClick={() => setCreateTemplate(t.id)}
                  className={`text-left p-3 rounded-lg border text-xs transition-all ${createTemplate === t.id ? "bg-coral/10 border-coral/30 text-coral" : "border-sand text-slate hover:border-coral/20"}`}>
                  <span className="font-semibold block">{t.name}</span>
                  <span className="opacity-70">{t.description}</span>
                </button>
              ))}
            </div>
          </div>

          {createError && <p className="text-red-500 text-xs">{createError}</p>}

          <button onClick={handleCreate} disabled={creating || !createTitle.trim() || !createSlug.trim()}
            className="w-full flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
            {creating ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><Plus size={14} /> Create Page</>}
          </button>
        </div>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2">
            <Layout size={22} className="text-coral" /> Landing Pages
          </h1>
          <p className="text-slate text-sm mt-0.5">Create opt-in pages that capture leads directly into your subscriber list.</p>
        </div>
        <button onClick={() => setView("create")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-coral hover:bg-coral-dark text-white text-sm font-semibold transition-colors">
          <Plus size={15} /> New Page
        </button>
      </div>

      {pages.length === 0 ? (
        <div className="bg-white rounded-xl border border-sand py-16 text-center">
          <Layout size={40} className="mx-auto text-slate/20 mb-3" />
          <h3 className="font-display font-bold text-ink text-lg">No Landing Pages Yet</h3>
          <p className="text-sm text-slate mt-1 max-w-sm mx-auto mb-5">
            Create opt-in pages with forms that automatically add subscribers to your list with tags.
          </p>
          <button onClick={() => setView("create")}
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
            <Plus size={14} /> Create Your First Page
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map(page => (
            <div key={page.id} className="bg-white rounded-xl border border-sand p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-paper flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-coral" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-sm font-bold text-ink truncate">{page.title}</h3>
                  <StatusBadge status={page.status} />
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate">
                  <span>/{page.slug}</span>
                  <span><Eye size={10} className="inline" /> {page.view_count} views</span>
                  <span><Users size={10} className="inline" /> {page.submission_count} leads</span>
                  {page.submission_count > 0 && page.view_count > 0 && (
                    <span className="text-coral font-semibold">
                      {((page.submission_count / page.view_count) * 100).toFixed(1)}% conversion
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {page.status === "published" && (
                  <button onClick={() => copyUrl(page.slug)} title="Copy URL"
                    className="p-2 text-slate hover:text-teal transition-colors"><Copy size={14} /></button>
                )}
                <button onClick={() => openSubs(page)} title="Submissions"
                  className="p-2 text-slate hover:text-coral transition-colors"><Users size={14} /></button>
                <button onClick={() => openEdit(page)} title="Edit"
                  className="p-2 text-slate hover:text-coral transition-colors"><Settings size={14} /></button>
                <button onClick={() => handleDelete(page.id)} title="Delete"
                  className="p-2 text-slate/40 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
